import { above } from '@sportson/core-web/utils/mediaqueries';
import { ArrowIcon } from 'assets/media/icons/ArrowIcon';
import Breadcrumbs from 'components/Breadcrumbs';
import SubCategories, { type SubCategory } from 'components/SubCategories/SubCategories';
import Text from 'components/Text';
import TextButton from 'components/TextButton';
import styled from 'libs/styled';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { type WordpressACFWysiwyg } from 'libs/wordpress/types/acf/fields';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Div = styled('div')``;

const BrandHeaderWrapper = styled('div')``;

const CategoryHeroTextArea = styled('div')`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 24px;
    position: relative;
    padding: 24px 0 40px 0;
`;

const DescriptionWrapper = styled('div')`
    grid-column: 1 / -1;

    ${above['desktop.sm']} {
        grid-column: 9 / span 4;
    }
`;

const DescriptionLimiter = styled('div')`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ descriptionVisible }) => (descriptionVisible ? 0 : 3)};
    margin-bottom: 16px;
`;

const CategoryHeroNavigation = styled('div')`
    display: flex;
    position: relative;

    ${above['desktop.sm']} {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 24px;
    }
`;

export type BrandHeaderProps = {
    title?: string;
    description?: string | WordpressACFWysiwyg;
    breadcrumbs: { permalink: string | null; title: string }[];
    subCategories?: SubCategory[];
};

const isTextClamped = (elm) => elm.scrollHeight > elm.clientHeight;

const BrandHeader = ({ title, description, breadcrumbs, subCategories }: BrandHeaderProps) => {
    const ref = useRef();
    const { t } = useTranslation();

    const [truncatedText, setTruncatedText] = useState(false);
    const [descriptionVisible, setDescriptionVisible] = useState(false);

    useEffect(() => {
        function checkTruncated() {
            setTruncatedText(isTextClamped(ref.current));
        }
        if (description !== null) {
            checkTruncated();
        }
    }, []);

    return (
        <BrandHeaderWrapper p={['0 12px', null, null, null, null, '0 40px']}>
            <Breadcrumbs
                gridColumn="standard"
                p={['16px 12px 16px 0px', null, null, null, '16px 0px']}
                breadcrumbs={breadcrumbs}
            />
            <CategoryHeroTextArea>
                {title && (
                    <Div gridColumn="1 / span 8" display="flex" flexDirection="column" gap="16px">
                        <Text
                            as="h1"
                            typography={['Content/32_120_0_500', null, null, null, null, 'Content/80_120_0_500']}
                        >
                            {title}
                        </Text>
                    </Div>
                )}
                {description && (
                    <DescriptionWrapper gridColumn="1 / span 8">
                        <DescriptionLimiter ref={ref} descriptionVisible={descriptionVisible}>
                            <Wysiwyg
                                data={description}
                                TextComponent={(props) => <Text typography="Content/20_140_0_350" {...props} />}
                            />
                        </DescriptionLimiter>
                        {truncatedText && (
                            <TextButton onClick={() => setDescriptionVisible(!descriptionVisible)}>
                                <Text as="span" typography="UI/16_100_0_450" mr="8px">
                                    {t('read_more')}
                                </Text>
                                <ArrowIcon size="xsmall" direction={descriptionVisible ? 'up' : 'down'} />
                            </TextButton>
                        )}
                    </DescriptionWrapper>
                )}
            </CategoryHeroTextArea>
            {subCategories && (
                <CategoryHeroNavigation>
                    <SubCategories subCategories={subCategories} />
                </CategoryHeroNavigation>
            )}
        </BrandHeaderWrapper>
    );
};

export default BrandHeader;
